import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { IconButton } from "@mui/material";
import {
  Activity,
  AlertCircle,
  CalendarDays,
  Copy,
  CreditCard,
  DollarSign,
  Maximize2,
  Menu,
  RefreshCw,
  Stethoscope,
  TrendingUp,
  Users,
  X,
} from "lucide-react";
import axios from "axios";
import DoctorHeader from "../doctor_dashboard/DoctorHeader";
import DoctorFooter from "../doctor_dashboard/DoctorFooter";
import DoctorSidebar, { DoctorSidebarDrawer } from "../doctor_dashboard/DoctorSidebar";
import SimpleSnackbar from "../Snackbar";

const todayKey = () => new Date().toISOString().slice(0, 10);

const daysAgoKey = (days) => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date.toISOString().slice(0, 10);
};

const numberFormat = new Intl.NumberFormat("en-NG");
const compactNumberFormat = new Intl.NumberFormat("en-NG", {
  notation: "compact",
  maximumFractionDigits: 1,
});
const currencyFormat = new Intl.NumberFormat("en-NG", {
  style: "currency",
  currency: "NGN",
  maximumFractionDigits: 0,
});

const formatNumber = (value) => numberFormat.format(Number(value || 0));
const formatCompact = (value) => compactNumberFormat.format(Number(value || 0));
const formatCurrency = (value) => currencyFormat.format(Number(value || 0));
const formatPercent = (value) => `${Number(value || 0).toFixed(1)}%`;
const formatDate = (value) => {
  if (!value) return "N/A";

  return new Date(value).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatDateTime = (value) => {
  if (!value) return "N/A";

  return new Date(value).toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const MetricCard = ({ label, value, note, icon: Icon, tone = "slate" }) => {
  const tones = {
    slate: "bg-slate-900 text-white",
    emerald: "bg-emerald-700 text-white",
    amber: "bg-amber-500 text-white",
    blue: "bg-sky-700 text-white",
    rose: "bg-rose-600 text-white",
  };

  return (
    <article className="rounded-2xl border border-slate-200 bg-white p-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-medium text-slate-500">{label}</p>
          <p className="mt-3 text-2xl font-semibold tracking-[-0.03em] text-slate-950 sm:text-3xl">
            {value}
          </p>
        </div>

        <div
          className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl ${
            tones[tone] || tones.slate
          }`}
        >
          <Icon size={20} />
        </div>
      </div>

      {note && <p className="mt-4 text-sm leading-6 text-slate-500">{note}</p>}
    </article>
  );
};

const LineChart = ({
  title,
  data = [],
  color = "#075E54",
  valuePrefix = "",
  onExpand,
  large = false,
}) => {
  const width = 720;
  const height = large ? 340 : 260;
  const padding = 36;
  const values = data.map((item) => Number(item.value || 0));
  const maxValue = Math.max(...values, 1);
  const minValue = Math.min(...values, 0);
  const range = Math.max(maxValue - minValue, 1);

  const points = data.map((item, index) => {
    const x =
      padding +
      (index / Math.max(data.length - 1, 1)) * (width - padding * 2);
    const y =
      height -
      padding -
      ((Number(item.value || 0) - minValue) / range) * (height - padding * 2);

    return { x, y, ...item };
  });

  const path = points
    .map((point, index) => `${index === 0 ? "M" : "L"} ${point.x} ${point.y}`)
    .join(" ");

  const lastValue = values[values.length - 1] || 0;
  const firstValue = values[0] || 0;
  const change = firstValue ? ((lastValue - firstValue) / firstValue) * 100 : 0;

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5">
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h3 className="text-base font-semibold text-slate-950">{title}</h3>
          <p className="mt-1 text-sm text-slate-500">
            Last point: {valuePrefix}
            {formatCompact(lastValue)}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <span
            className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
              change >= 0
                ? "bg-emerald-50 text-emerald-700"
                : "bg-rose-50 text-rose-700"
            }`}
          >
            {change >= 0 ? "+" : ""}
            {change.toFixed(1)}%
          </span>

          {onExpand && (
            <button
              type="button"
              onClick={onExpand}
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-500 transition hover:bg-slate-50 hover:text-slate-950"
              aria-label={`Expand ${title}`}
            >
              <Maximize2 size={15} />
            </button>
          )}
        </div>
      </div>

      <svg
        viewBox={`0 0 ${width} ${height}`}
        className={`${large ? "h-[360px]" : "h-64"} w-full`}
      >
        <line
          x1={padding}
          x2={width - padding}
          y1={height - padding}
          y2={height - padding}
          stroke="#e2e8f0"
          strokeWidth="1"
        />
        <line
          x1={padding}
          x2={padding}
          y1={padding}
          y2={height - padding}
          stroke="#e2e8f0"
          strokeWidth="1"
        />
        {[0.25, 0.5, 0.75].map((step) => {
          const y = padding + step * (height - padding * 2);

          return (
            <line
              key={step}
              x1={padding}
              x2={width - padding}
              y1={y}
              y2={y}
              stroke="#f1f5f9"
              strokeWidth="1"
            />
          );
        })}
        <path d={path} fill="none" stroke={color} strokeWidth="4" />
        {points.map((point, index) => {
          const interval = large ? 4 : 12;

          if (index !== 0 && index !== points.length - 1 && index % interval !== 0) {
            return null;
          }

          return (
            <g key={`${point.key}-${index}`}>
              <circle cx={point.x} cy={point.y} r="4" fill={color} />
              <text
                x={point.x}
                y={height - 12}
                textAnchor="middle"
                className="fill-slate-400 text-[11px]"
              >
                {point.label}
              </text>
            </g>
          );
        })}
      </svg>
    </section>
  );
};

const BarList = ({ title, data = [], valueKey = "value", money = false }) => {
  const max = Math.max(...data.map((item) => Number(item[valueKey] || 0)), 1);

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5">
      <h3 className="text-base font-semibold text-slate-950">{title}</h3>

      <div className="mt-5 space-y-4">
        {data.length === 0 && (
          <p className="text-sm text-slate-500">No data for this period.</p>
        )}

        {data.map((item) => {
          const value = Number(item[valueKey] || 0);

          return (
            <div key={item.label || item.doctorId}>
              <div className="mb-2 flex items-center justify-between gap-3">
                <p className="truncate text-sm font-medium text-slate-700">
                  {item.label || item.doctorName}
                </p>
                <p className="shrink-0 text-sm font-semibold text-slate-950">
                  {money ? formatCurrency(value) : formatNumber(value)}
                </p>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                <div
                  className="h-full rounded-full bg-slate-900"
                  style={{ width: `${Math.max((value / max) * 100, 4)}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

const SkeletonBlock = ({ className = "" }) => (
  <div className={`animate-pulse rounded-2xl bg-slate-200/70 ${className}`} />
);

const DashboardSkeleton = () => (
  <div className="space-y-6">
    <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {[0, 1, 2, 3, 4].map((item) => (
        <div
          key={item}
          className="rounded-2xl border border-slate-200 bg-white p-5"
        >
          <SkeletonBlock className="h-4 w-24" />
          <SkeletonBlock className="mt-5 h-9 w-32" />
          <SkeletonBlock className="mt-5 h-4 w-full" />
        </div>
      ))}
    </section>

    <section className="grid grid-cols-1 gap-5 xl:grid-cols-2">
      <SkeletonBlock className="h-80 border border-slate-200 bg-white" />
      <SkeletonBlock className="h-80 border border-slate-200 bg-white" />
    </section>

    <section className="grid grid-cols-1 gap-5 xl:grid-cols-3">
      <SkeletonBlock className="h-72 border border-slate-200 bg-white" />
      <SkeletonBlock className="h-72 border border-slate-200 bg-white" />
      <SkeletonBlock className="h-72 border border-slate-200 bg-white" />
    </section>
  </div>
);

const ModalShell = ({ title, subtitle, onClose, children, wide = false }) => (
  <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/45 px-4 py-6">
    <section
      className={`max-h-[90vh] w-full overflow-hidden rounded-2xl bg-white shadow-2xl ${
        wide ? "max-w-6xl" : "max-w-4xl"
      }`}
    >
      <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-950">{title}</h2>
          {subtitle && (
            <p className="mt-1 text-sm leading-6 text-slate-500">{subtitle}</p>
          )}
        </div>

        <button
          type="button"
          onClick={onClose}
          className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-slate-200 text-slate-500 transition hover:bg-slate-50 hover:text-slate-950"
          aria-label="Close"
        >
          <X size={17} />
        </button>
      </div>

      <div className="max-h-[calc(90vh-84px)] overflow-y-auto p-5">
        {children}
      </div>
    </section>
  </div>
);

const PaymentsTable = ({ payments = [], showFees = false }) => (
  <div className="overflow-x-auto">
    <table className="w-full min-w-[860px] text-left text-sm">
      <thead>
        <tr className="border-b border-slate-200 text-xs uppercase text-slate-400">
          <th className="py-3 font-semibold">Patient</th>
          <th className="py-3 font-semibold">Type</th>
          <th className="py-3 font-semibold">Amount</th>
          {showFees && <th className="py-3 font-semibold">Net</th>}
          <th className="py-3 font-semibold">Channel</th>
          <th className="py-3 font-semibold">Reference</th>
          <th className="py-3 font-semibold">Date</th>
        </tr>
      </thead>
      <tbody>
        {payments.map((payment) => (
          <tr
            key={payment.reference}
            className="border-b border-slate-100 text-slate-700"
          >
            <td className="py-3">
              <p className="font-medium text-slate-950">
                {payment.patientName || "Patient"}
              </p>
              <p className="text-xs text-slate-400">
                {payment.patientEmail || "No email"}
              </p>
            </td>
            <td className="py-3">{payment.label || "Payment"}</td>
            <td className="py-3 font-semibold text-slate-950">
              {formatCurrency(payment.amount)}
            </td>
            {showFees && (
              <td className="py-3">
                <p className="font-semibold text-slate-950">
                  {formatCurrency(payment.netAmount)}
                </p>
                <p className="text-xs text-slate-400">
                  Fees: {formatCurrency(payment.fees)}
                </p>
              </td>
            )}
            <td className="py-3 capitalize">{payment.channel || "N/A"}</td>
            <td className="py-3 text-xs text-slate-500">
              {payment.reference}
            </td>
            <td className="py-3">{formatDate(payment.paidAt)}</td>
          </tr>
        ))}

        {!payments.length && (
          <tr>
            <td
              colSpan={showFees ? 7 : 6}
              className="py-10 text-center text-slate-500"
            >
              No Paystack payments found for this period.
            </td>
          </tr>
        )}
      </tbody>
    </table>
  </div>
);

const AdminDashboard = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [startDate, setStartDate] = useState(daysAgoKey(364));
  const [endDate, setEndDate] = useState(todayKey());
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedChart, setExpandedChart] = useState(null);
  const [paymentsOpen, setPaymentsOpen] = useState(false);
  const [snackMessage, setSnackMessage] = useState("");
  const [snackColor, setSnackColor] = useState("success");
  const hasLoadedRef = useRef(false);

  const headers = useMemo(
    () => ({
      Authorization: `Bearer ${localStorage.getItem("token") || null}`,
    }),
    []
  );

  const fetchDashboard = useCallback(
    async ({ silent = false } = {}) => {
      try {
        if (!silent && !hasLoadedRef.current) setLoading(true);

        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/admin/dashboard`,
          {
            params: { startDate, endDate },
            headers,
          }
        );

        if (!res.data?.success) {
          throw new Error("Dashboard could not load");
        }

        setDashboard(res.data);
        hasLoadedRef.current = true;
      } catch (error) {
        console.error(error);
        setSnackMessage(
          error.response?.data?.message || "Could not load admin dashboard"
        );
        setSnackColor("error");
      } finally {
        setLoading(false);
      }
    },
    [endDate, headers, startDate]
  );

  useEffect(() => {
    fetchDashboard({ silent: hasLoadedRef.current });
  }, [fetchDashboard]);

  const totals = useMemo(() => dashboard?.totals || {}, [dashboard]);
  const trends = useMemo(() => dashboard?.trends || {}, [dashboard]);
  const breakdowns = useMemo(() => dashboard?.breakdowns || {}, [dashboard]);
  const payments = useMemo(() => dashboard?.payments || [], [dashboard]);
  const usingDatabasePayments =
    dashboard?.paystack?.paymentDataSource === "database_payment_records";
  const paymentSourceLabel = usingDatabasePayments
    ? "recorded payments"
    : "Paystack payments";
  const granularity = dashboard?.filters?.granularity || "day";
  const granularityLabel =
    granularity === "month"
      ? "Monthly"
      : granularity === "week"
      ? "Weekly"
      : "Daily";

  const periodPresets = useMemo(
    () => [
      { label: "30 days", days: 29 },
      { label: "90 days", days: 89 },
      { label: "6 months", days: 179 },
      { label: "1 year", days: 364 },
    ],
    []
  );

  const chartConfigs = useMemo(
    () => [
      {
        id: "revenue",
        title: `${usingDatabasePayments ? "Recorded" : "Paystack"} revenue trend`,
        data: trends.revenue || [],
        color: "#047857",
        valuePrefix: "NGN ",
      },
      {
        id: "patients",
        title: "Patient growth",
        data: trends.patientGrowth || [],
        color: "#0f172a",
      },
      {
        id: "consultations",
        title: "Consultations",
        data: trends.consultations || [],
        color: "#0369a1",
      },
      {
        id: "payments",
        title: usingDatabasePayments ? "Recorded payments" : "Paystack payments",
        data: trends.payments || [],
        color: "#b45309",
      },
    ],
    [trends, usingDatabasePayments]
  );

  const investorSummary = useMemo(() => {
    return [
      `Zaviramed dashboard (${startDate} to ${endDate})`,
      `Patients: ${formatNumber(totals.patients)}`,
      `New patients: ${formatNumber(totals.newPatients)}`,
      `${usingDatabasePayments ? "Recorded" : "Paystack"} payments: ${formatNumber(totals.payments)}`,
      `${usingDatabasePayments ? "Recorded" : "Paystack"} gross revenue: ${formatCurrency(totals.grossRevenue || totals.revenue)}`,
      `Paystack net revenue: ${formatCurrency(totals.netRevenue)}`,
      `Consultations: ${formatNumber(totals.consultations)}`,
      `Doctors: ${formatNumber(totals.doctors)} (${formatNumber(
        totals.verifiedDoctors
      )} verified)`,
      `Patient-to-paid conversion: ${formatPercent(
        totals.patientToPaidConversion
      )}`,
    ].join("\n");
  }, [endDate, startDate, totals, usingDatabasePayments]);

  const copySummary = async () => {
    try {
      await navigator.clipboard.writeText(investorSummary);
      setSnackMessage("Investor summary copied");
      setSnackColor("success");
    } catch (error) {
      setSnackMessage("Could not copy summary");
      setSnackColor("error");
    }
  };

  const applyPreset = (days) => {
    setStartDate(daysAgoKey(days));
    setEndDate(todayKey());
  };

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <DoctorHeader />

      <SimpleSnackbar
        message={snackMessage}
        color={snackColor}
        onClose={() => setSnackMessage("")}
      />

      <div className="flex items-center justify-between border-b border-slate-200 bg-white px-4 py-3 md:hidden">
        <div>
          <p className="text-xs text-slate-400">Admin</p>
          <h1 className="text-base font-semibold text-slate-950">Dashboard</h1>
        </div>

        <IconButton onClick={() => setDrawerOpen(true)}>
          <Menu />
        </IconButton>
      </div>

      <div className="flex flex-1 overflow-hidden">
        <div className="hidden lg:flex">
          <DoctorSidebar activeTab="Admin Dashboard" setActiveTab={() => {}} />
        </div>

        <DoctorSidebarDrawer
          open={drawerOpen}
          onOpen={() => setDrawerOpen(true)}
          onClose={() => setDrawerOpen(false)}
          activeTab="Admin Dashboard"
          setActiveTab={() => setDrawerOpen(false)}
        />

        <main className="flex-1 overflow-y-auto">
          <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
            <div className="mb-6 flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
              <div>
               
                <h1 className="mt-2 text-2xl font-semibold tracking-[-0.03em] text-slate-950 sm:text-3xl">
                 For Admin
                </h1>
                <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500">
                  View patient growth, revenue, payments,
                  consultations, and doctor capacity.
                </p>
              </div>

              <div className="flex w-full flex-col gap-3 lg:w-auto">
                <div className="flex flex-wrap items-center gap-2">
                  {periodPresets.map((preset) => (
                    <button
                      key={preset.label}
                      type="button"
                      onClick={() => applyPreset(preset.days)}
                      className="h-9 rounded-xl border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-600 transition hover:bg-slate-50 hover:text-slate-950"
                    >
                      {preset.label}
                    </button>
                  ))}

                  <span className="rounded-xl bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-600">
                    {granularityLabel} charts
                  </span>

                  {loading && dashboard && (
                    <span className="rounded-xl bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700">
                      Updating
                    </span>
                  )}
                </div>

                <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                  <label className="block">
                    <span className="mb-2 block text-xs font-medium text-slate-500">
                      Start date
                    </span>
                    <span className="flex h-11 items-center gap-2 rounded-xl border border-slate-200 bg-white px-3">
                      <CalendarDays size={16} className="text-slate-400" />
                      <input
                        type="date"
                        value={startDate}
                        onChange={(event) => setStartDate(event.target.value)}
                        className="bg-transparent text-sm text-slate-800 outline-none"
                      />
                    </span>
                  </label>

                  <label className="block">
                    <span className="mb-2 block text-xs font-medium text-slate-500">
                      End date
                    </span>
                    <span className="flex h-11 items-center gap-2 rounded-xl border border-slate-200 bg-white px-3">
                      <CalendarDays size={16} className="text-slate-400" />
                      <input
                        type="date"
                        value={endDate}
                        onChange={(event) => setEndDate(event.target.value)}
                        className="bg-transparent text-sm text-slate-800 outline-none"
                      />
                    </span>
                  </label>

                  <button
                    type="button"
                    onClick={() => fetchDashboard({ silent: true })}
                    className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 text-sm font-medium text-white transition hover:bg-slate-800"
                  >
                    <RefreshCw size={16} />
                    Refresh
                  </button>

                  <button
                    type="button"
                    onClick={copySummary}
                    className="inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
                  >
                    <Copy size={16} />
                    Copy summary
                  </button>
                </div>
              </div>
            </div>

            {loading && !dashboard ? (
              <DashboardSkeleton />
            ) : (
              <div className="space-y-6">
                <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  <MetricCard
                    label="Total patients"
                    value={formatNumber(totals.patients)}
                    note={`${formatNumber(totals.newPatients)} joined in period`}
                    icon={Users}
                    tone="slate"
                  />
                  <MetricCard
                    label={usingDatabasePayments ? "Recorded revenue" : "Paystack revenue"}
                    value={formatCurrency(totals.grossRevenue || totals.revenue)}
                    note={`${formatCurrency(
                      totals.netRevenue
                    )} ${
                      usingDatabasePayments
                        ? "net recorded"
                        : "net after Paystack fees"
                    }`}
                    icon={DollarSign}
                    tone="emerald"
                  />
                  <MetricCard
                    label={usingDatabasePayments ? "Recorded payments" : "Paystack payments"}
                    value={formatNumber(totals.payments)}
                    note={`${formatCurrency(
                      totals.averageRevenuePerPayment
                    )} average successful payment`}
                    icon={CreditCard}
                    tone="amber"
                  />
                  <MetricCard
                    label="Consultations"
                    value={formatNumber(totals.consultations)}
                    note={`${formatPercent(
                      totals.patientToPaidConversion
                    )} patient-to-paid conversion`}
                    icon={Activity}
                    tone="blue"
                  />
                  <MetricCard
                    label="Doctors"
                    value={formatNumber(totals.doctors)}
                    note={`${formatNumber(
                      totals.verifiedDoctors
                    )} verified, ${formatNumber(
                      totals.availableDoctors
                    )} with calendar set`}
                    icon={Stethoscope}
                    tone="rose"
                  />
                </section>

                <section className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <article className="rounded-2xl border border-slate-200 bg-white p-4">
                    <p className="text-xs font-semibold uppercase text-slate-400">
                      Payment source
                    </p>
                    <p className="mt-2 text-sm font-semibold text-slate-950">
                      {dashboard?.paystack?.status === "error"
                        ? dashboard?.paystack?.keyMode === "test"
                          ? "Paystack test key is configured"
                          : "Paystack could not load"
                        : dashboard?.paystack?.source || "Paystack API"}
                    </p>
                    <p className="mt-1 text-sm leading-6 text-slate-500">
                      Revenue, payment count, graphs, and rows use successful
                      live Paystack transactions first. If Paystack is not
                      available locally, this page uses backend payment records.
                    </p>
                  </article>

                  <article className="rounded-2xl border border-slate-200 bg-white p-4">
                    <p className="text-xs font-semibold uppercase text-slate-400">
                      Period
                    </p>
                    <p className="mt-2 text-sm font-semibold text-slate-950">
                      {formatDate(dashboard?.filters?.startDate)} to{" "}
                      {formatDate(dashboard?.filters?.endDate)}
                    </p>
                    <p className="mt-1 text-sm leading-6 text-slate-500">
                      Graphs are grouped by {granularityLabel.toLowerCase()}{" "}
                      points for this date range.
                    </p>
                  </article>

                  <article className="rounded-2xl border border-slate-200 bg-white p-4">
                    <p className="text-xs font-semibold uppercase text-slate-400">
                      Paid conversion
                    </p>
                    <p className="mt-2 text-sm font-semibold text-slate-950">
                      {formatPercent(totals.patientToPaidConversion)}
                    </p>
                    <p className="mt-1 text-sm leading-6 text-slate-500">
                      {formatNumber(totals.paidPatients)} patients have paid
                      before, {formatNumber(totals.activePaidPatients)} are
                      active now.
                    </p>
                  </article>
                </section>

                {(dashboard?.paystack?.error || dashboard?.paystack?.warning) && (
                  <section className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                    <AlertCircle size={18} className="mt-0.5 shrink-0" />
                    <p>
                      {dashboard?.paystack?.error ||
                        dashboard?.paystack?.warning}
                    </p>
                  </section>
                )}

                <section className="grid grid-cols-1 gap-5 xl:grid-cols-2">
                  {chartConfigs.map((chart) => (
                    <LineChart
                      key={chart.id}
                      title={chart.title}
                      data={chart.data}
                      color={chart.color}
                      valuePrefix={chart.valuePrefix}
                      onExpand={() => setExpandedChart(chart)}
                    />
                  ))}
                </section>

                <section className="grid grid-cols-1 gap-5 xl:grid-cols-3">
                  <BarList
                    title="Revenue by payment type"
                    data={breakdowns.paymentMix || []}
                    valueKey="revenue"
                    money
                  />
                  <BarList
                    title="How patients heard about us"
                    data={breakdowns.acquisitionSources || []}
                  />
                  <BarList
                    title="Top doctors by consultations"
                    data={dashboard?.topDoctors || []}
                    valueKey="consultations"
                  />
                </section>

                <section className="rounded-2xl border border-slate-200 bg-white p-5">
                  <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-center gap-2">
                      <TrendingUp size={18} className="text-slate-500" />
                      <div>
                        <h3 className="text-base font-semibold text-slate-950">
                          Recent {paymentSourceLabel}
                        </h3>
                        <p className="mt-1 text-sm text-slate-500">
                          Showing the latest 10 of {formatNumber(totals.payments)}.
                        </p>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => setPaymentsOpen(true)}
                      className="inline-flex h-10 items-center justify-center rounded-xl border border-slate-200 px-4 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                    >
                      View all payments
                    </button>
                  </div>

                  <PaymentsTable payments={dashboard?.recentPayments || []} />
                </section>
              </div>
            )}
          </div>
        </main>
      </div>

      {expandedChart && (
        <ModalShell
          title={expandedChart.title}
          subtitle={`${granularityLabel} view from ${formatDate(
            dashboard?.filters?.startDate
          )} to ${formatDate(dashboard?.filters?.endDate)}.`}
          onClose={() => setExpandedChart(null)}
          wide
        >
          <LineChart
            title={expandedChart.title}
            data={expandedChart.data}
            color={expandedChart.color}
            valuePrefix={expandedChart.valuePrefix}
            large
          />
        </ModalShell>
      )}

      {paymentsOpen && (
        <ModalShell
          title={`All ${paymentSourceLabel}`}
          subtitle={`${formatNumber(
            payments.length
          )} fetched successful payments from ${formatDate(
            dashboard?.filters?.startDate
          )} to ${formatDate(
            dashboard?.filters?.endDate
          )}.`}
          onClose={() => setPaymentsOpen(false)}
          wide
        >
          <section className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="rounded-2xl border border-slate-200 p-4">
              <p className="text-xs font-semibold uppercase text-slate-400">
                Gross revenue
              </p>
              <p className="mt-2 text-lg font-semibold text-slate-950">
                {formatCurrency(totals.grossRevenue || totals.revenue)}
              </p>
            </div>
            <div className="rounded-2xl border border-slate-200 p-4">
              <p className="text-xs font-semibold uppercase text-slate-400">
                Net revenue
              </p>
              <p className="mt-2 text-lg font-semibold text-slate-950">
                {formatCurrency(totals.netRevenue)}
              </p>
            </div>
            <div className="rounded-2xl border border-slate-200 p-4">
              <p className="text-xs font-semibold uppercase text-slate-400">
                Paystack fees
              </p>
              <p className="mt-2 text-lg font-semibold text-slate-950">
                {formatCurrency(totals.paystackFees)}
              </p>
            </div>
          </section>

          <PaymentsTable payments={payments} showFees />

          <p className="mt-4 text-xs text-slate-400">
            Opened {formatDateTime(new Date())}
          </p>
        </ModalShell>
      )}

    </div>
  );
};

export default AdminDashboard;
